import { create } from 'zustand';
import { pauseAtcForCrewVoice } from '../conditions/AtcPlayback';
import { setCrewVoiceAudioActive } from '../../lib/speech';

export interface VoicePeer {id:string;userId:string;name:string}
export interface VoiceState {missionId:string|null;status:'off'|'connecting'|'joined'|'error';peers:VoicePeer[];speaker:(VoicePeer&{sampleRate:number;/** You blocked this speaker: the server sends you none of their audio. */blocked?:boolean})|null;myId:string|null;error:string|null;microphone:boolean}
const initial:VoiceState={missionId:null,status:'off',peers:[],speaker:null,myId:null,error:null,microphone:false};
export const useCrewVoice=create<VoiceState>(()=>initial);

/** Foreground half-duplex media. Audio is relayed without recording or persistence. */
class CrewVoiceSession {
 private ws:WebSocket|null=null;
 private context:AudioContext|null=null;
 private stream:MediaStream|null=null;
 private node:AudioWorkletNode|null=null;
 private source:MediaStreamAudioSourceNode|null=null;
 private wantsTalk=false;
 private acquiring=false;
 private epoch=0;
 private moduleLoaded=false;
 private nextAudio=0;
 private timer:ReturnType<typeof setInterval>|null=null;
 private restoreAtc:(()=>void)|null=null;
 private playing=new Set<AudioBufferSourceNode>();
 private stopOnBlur=()=>this.stop();
 private stopOnHidden=()=>{if(document.hidden)this.stop();};
 async join(missionId:string){
  this.leave();const epoch=++this.epoch;
  if(!isSecureContext||!window.AudioContext||!navigator.mediaDevices?.getUserMedia){useCrewVoice.setState({...initial,status:'error',error:'Crew voice needs HTTPS, microphone access and Web Audio support.'});return;}
  try{
   const context=new AudioContext({sampleRate:24000});this.context=context;await context.resume();
   if(epoch!==this.epoch)return;
   const protocol=location.protocol==='https:'?'wss:':'ws:';
   useCrewVoice.setState({...initial,missionId,status:'connecting'});
   const ws=new WebSocket(`${protocol}//${location.host}/api/ws/crew-voice?missionId=${encodeURIComponent(missionId)}`);this.ws=ws;ws.binaryType='arraybuffer';
   ws.onopen=()=>{if(epoch!==this.epoch)return;this.timer=setInterval(()=>this.send({type:'ping'}),5000);};
   ws.onmessage=e=>{if(epoch!==this.epoch)return;if(e.data instanceof ArrayBuffer){this.receive(e.data);return;}
    let m:Record<string,unknown>;try{m=JSON.parse(e.data);}catch{return;}
    if(m.type==='hello')useCrewVoice.setState({myId:String(m.id),status:'joined'});
    if(m.type==='busy'){this.stop();useCrewVoice.setState({error:`${String(m.name)} is speaking. Try again when the channel is clear.`});}
    if(m.type==='state'){
     const previous=useCrewVoice.getState().speaker,speaker=m.speaker as VoiceState['speaker'];useCrewVoice.setState({peers:m.peers as VoicePeer[],speaker});
     // a blocked speaker still holds the half-duplex channel, but you hear nothing, so ATC keeps playing
     const audible=!!speaker&&!speaker.blocked;setCrewVoiceAudioActive(audible);
     if(audible){this.restoreAtc??=pauseAtcForCrewVoice();}else{this.restoreAtc?.();this.restoreAtc=null;}
     const mine=useCrewVoice.getState().myId;
     if(this.wantsTalk&&((speaker&&speaker.id!==mine)||(previous?.id===mine&&speaker?.id!==mine)))this.stop();
    }
   };
   ws.onclose=()=>{if(epoch!==this.epoch)return;this.leave();useCrewVoice.setState({status:'error',error:'Crew voice disconnected. Rejoin the channel to reconnect.'});};
   ws.onerror=()=>{if(epoch===this.epoch)useCrewVoice.setState({error:'Crew voice connection unavailable.'});};
   window.addEventListener('blur',this.stopOnBlur);document.addEventListener('visibilitychange',this.stopOnHidden);
  }catch{this.leave();useCrewVoice.setState({status:'error',error:'Audio could not start. Check browser audio permissions and rejoin.'});}
 }
 private send(value:unknown){if(this.ws?.readyState===WebSocket.OPEN)this.ws.send(JSON.stringify(value));}
 async talk(){
  if(this.acquiring||this.wantsTalk||useCrewVoice.getState().status!=='joined')return;
  if(useCrewVoice.getState().speaker){useCrewVoice.setState({error:'The channel is busy. Wait until the speaker finishes.'});return;}
  const context=this.context;if(!context)return;const epoch=this.epoch;this.wantsTalk=true;this.acquiring=true;
  try{
   useCrewVoice.setState({error:null});const stream=await navigator.mediaDevices.getUserMedia({audio:{channelCount:1,echoCancellation:true,noiseSuppression:true},video:false});
   if(!this.wantsTalk||epoch!==this.epoch){stream.getTracks().forEach(t=>t.stop());return;}
   this.stream=stream;useCrewVoice.setState({microphone:true});
   if(!this.moduleLoaded){await context.audioWorklet.addModule('/crew-voice-processor.js');if(epoch!==this.epoch)return;this.moduleLoaded=true;}
   await context.resume();if(epoch!==this.epoch)return;if(!this.wantsTalk){this.stop();return;}
   if(![16000,24000,44100,48000].includes(context.sampleRate))throw new Error('Unsupported audio rate');
   this.source=context.createMediaStreamSource(stream);this.node=new AudioWorkletNode(context,'sightline-crew-voice');
   this.node.port.onmessage=e=>{if(this.wantsTalk&&useCrewVoice.getState().speaker?.id===useCrewVoice.getState().myId&&this.ws?.readyState===WebSocket.OPEN){
     if(this.ws.bufferedAmount>64*1024){this.stop();useCrewVoice.setState({error:'Connection too slow for live voice. Release and try again.'});return;}
     this.ws.send(e.data);
   }};
   this.source.connect(this.node);this.node.connect(context.destination);this.send({type:'talk',sampleRate:context.sampleRate});
  }catch{if(epoch===this.epoch){this.stop();useCrewVoice.setState({error:'Microphone unavailable. Check permission and try again.'});}}
  finally{this.acquiring=false;}
 }
 stop(){
  this.wantsTalk=false;this.node?.disconnect();this.source?.disconnect();this.node=null;this.source=null;
  this.stream?.getTracks().forEach(t=>t.stop());this.stream=null;useCrewVoice.setState({microphone:false});this.send({type:'release'});
 }
 private receive(data:ArrayBuffer){
  const context=this.context,s=useCrewVoice.getState().speaker;if(!context||!s||s.id===useCrewVoice.getState().myId||data.byteLength>4096||data.byteLength%2)return;
  if(context.state!=='running'){useCrewVoice.setState({error:'Audio is paused by the device. Rejoin to resume listening.'});return;}
  const samples=data.byteLength/2,buffer=context.createBuffer(1,samples,s.sampleRate),out=buffer.getChannelData(0),view=new DataView(data);
  for(let i=0;i<samples;i++)out[i]=view.getInt16(i*2,true)/32768;
  if(this.nextAudio>context.currentTime+.3){useCrewVoice.setState({error:'Voice arrived too late; some audio was dropped.'});return;}
  this.nextAudio=Math.max(context.currentTime+.02,this.nextAudio);const source=context.createBufferSource();source.buffer=buffer;source.connect(context.destination);
  this.playing.add(source);source.onended=()=>{source.disconnect();this.playing.delete(source);};source.start(this.nextAudio);this.nextAudio+=buffer.duration;
 }
 leave(){
  this.epoch++;this.stop();const ws=this.ws;this.ws=null;if(ws){ws.onclose=null;ws.close();}
  if(this.timer)clearInterval(this.timer);this.timer=null;
  for(const source of this.playing){try{source.stop();}catch{}}this.playing.clear();
  void this.context?.close().catch(()=>{});this.context=null;this.moduleLoaded=false;this.nextAudio=0;
  this.restoreAtc?.();this.restoreAtc=null;
  setCrewVoiceAudioActive(false);
  window.removeEventListener('blur',this.stopOnBlur);document.removeEventListener('visibilitychange',this.stopOnHidden);
  useCrewVoice.setState(initial);
 }
}
export const crewVoice=new CrewVoiceSession();
